import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import SEO from "../components/SEO";
import CTASection from "../components/CTASection";
import { Reveal, MaskedLine, Eyebrow } from "../components/Reveal";
import { IMAGES, CAPABILITIES } from "../data/site";

const SOLUTIONS_DETAIL = [
    {
        id: "port-drayage",
        title: "Port Drayage",
        image: IMAGES.portTerminal,
        alt: "Container terminal operations at the Ports of Los Angeles and Long Beach",
        copy: "High-volume container transportation serving the Ports of Los Angeles and Long Beach. With 100+ trucks, 500+ chassis and a dual-yard operation, Pacific keeps containers flowing through the busiest port complex in the country.",
        points: [
            "High-volume LA/LB container moves",
            "500+ chassis to outlast chassis shortages",
            "Dual-yard flexibility near the port complex",
            "900+ containers moved every week",
        ],
    },
    {
        id: "transloading",
        title: "Transloading & Cross-Docking",
        image: IMAGES.forklift,
        alt: "Forklift transferring freight during a transload operation",
        copy: "Efficient container unloading, freight transfer and preparation for onward transportation — plus rapid cross-dock consolidation designed to reduce storage time and keep shipments moving.",
        points: [
            "Container unloading and freight transfer",
            "Cross-dock consolidation for faster turns",
            "Preparation for onward transportation",
            "Reduced dwell and storage time",
        ],
    },
    {
        id: "overweight-oog",
        title: "Overweight & OOG Handling",
        image: IMAGES.containerStacks,
        alt: "Heavy containers staged for overweight and out-of-gauge freight handling",
        copy: "Solutions for overweight, heavy, oversized and difficult freight — including overweight corridor operations and in-house weight reduction and rework that get problem loads legal and moving.",
        points: [
            "Overweight corridor capability",
            "90K top pick for heavy container handling",
            "Weight reduction and rework",
            "Block & bracing and container flipping",
        ],
    },
    {
        id: "warehousing",
        title: "Warehousing & Storage",
        image: IMAGES.warehouseForklift,
        alt: "Warehouse storage with forklift and palletized inventory",
        copy: "Short- and long-term storage backed by real inventory discipline — control, fulfillment and rework services that keep product moving instead of sitting.",
        points: [
            "Short- and long-term storage",
            "Inventory control and reporting",
            "Pick-and-pack fulfillment",
            "Kitting, rework and weight reduction",
        ],
    },
    {
        id: "dedicated",
        title: "Dedicated Transportation",
        image: IMAGES.trucksHighway,
        alt: "Dedicated Class 8 fleet running Southern California freight routes",
        copy: "Dedicated fleet capacity for customers requiring consistent transportation support. Your freight moves on Pacific equipment, with Pacific drivers, on your schedule.",
        points: [
            "Consistent dedicated capacity",
            "Pacific trucks, chassis and drivers",
            "High-volume account support",
            "One accountable logistics relationship",
        ],
    },
    {
        id: "final-mile",
        title: "Final Mile & OTR",
        image: IMAGES.semiHighway,
        alt: "Semi truck running long-distance over-the-road freight",
        copy: "Commercial and white-glove final-mile transportation, long-distance OTR capacity beyond Southern California, and coordinated air and rail freight support.",
        points: [
            "Commercial and white-glove final mile",
            "Long-distance OTR transportation",
            "Air and rail freight coordination",
            "Residential freight capability",
        ],
    },
];

export default function Solutions() {
    return (
        <>
            <SEO
                title="Logistics Solutions | Port Drayage, Transloading & Warehousing | Pacific Expressway"
                description="Port drayage, transloading, cross-docking, overweight & OOG handling, warehousing, dedicated transportation, final mile and OTR — integrated logistics for Southern California supply chains."
                path="/solutions"
            />
            <section
                className="relative flex min-h-[62svh] items-end overflow-hidden bg-ink"
                data-testid="solutions-hero"
            >
                <div className="absolute inset-0" aria-hidden="true">
                    <img src={IMAGES.portStacks} alt="" className="h-full w-full object-cover opacity-50" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/70 to-ink/40" aria-hidden="true" />
                <div className="container-px relative w-full pb-14 pt-40 lg:pb-20">
                    <Eyebrow>Our Solutions</Eyebrow>
                    <h1 className="mt-6 max-w-4xl font-heading text-3xl sm:text-4xl lg:text-[56px] font-bold tracking-tight leading-[1.15] sm:leading-[1.1] lg:leading-[1.06] text-white">
                        <MaskedLine delay={0.2}>Logistics Solutions Built</MaskedLine>
                        <MaskedLine delay={0.32}>
                            to Keep Freight Moving<span className="text-gold">.</span>
                        </MaskedLine>
                    </h1>
                    <Reveal delay={0.5} className="mt-6 max-w-2xl">
                        <p className="text-base sm:text-lg leading-relaxed text-gray-300">
                            From the ports to the warehouse and final destination, Pacific
                            Expressway provides integrated logistics support for demanding
                            Southern California supply chains.
                        </p>
                    </Reveal>
                </div>
            </section>

            <section className="bg-white py-16 sm:py-20 lg:py-24">
                <div className="container-px space-y-20 lg:space-y-28">
                    {SOLUTIONS_DETAIL.map((solution, i) => (
                        <article
                            key={solution.id}
                            id={solution.id}
                            data-testid={`solution-section-${solution.id}`}
                            className="grid scroll-mt-28 items-center gap-10 lg:grid-cols-2 lg:gap-16"
                        >
                            <Reveal className={i % 2 === 1 ? "lg:order-2" : ""}>
                                <div className="group relative overflow-hidden">
                                    <img
                                        src={solution.image}
                                        alt={solution.alt}
                                        loading="lazy"
                                        className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                                    />
                                    <span
                                        className="absolute left-0 top-0 bg-gold px-4 py-2 font-heading text-sm font-bold tracking-[0.2em] text-ink"
                                        aria-hidden="true"
                                    >
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                </div>
                            </Reveal>
                            <Reveal delay={0.1} className={i % 2 === 1 ? "lg:order-1" : ""}>
                                <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-ink">
                                    {solution.title}
                                </h2>
                                <p className="mt-5 text-base sm:text-lg leading-relaxed text-gray-600">
                                    {solution.copy}
                                </p>
                                <ul className="mt-7 grid gap-3 sm:grid-cols-2">
                                    {solution.points.map((point) => (
                                        <li key={point} className="flex items-start gap-2.5">
                                            <Check size={17} className="mt-1 shrink-0 text-gold" aria-hidden="true" />
                                            <span className="text-[15px] font-medium text-gray-700">{point}</span>
                                        </li>
                                    ))}
                                </ul>
                                <Link
                                    to="/contact"
                                    data-testid={`solution-quote-${solution.id}`}
                                    className="group mt-8 inline-flex items-center gap-2 font-heading text-sm font-bold uppercase tracking-[0.16em] text-ink transition-colors hover:text-gold"
                                >
                                    Request This Service
                                    <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" />
                                </Link>
                            </Reveal>
                        </article>
                    ))}
                </div>
            </section>

            <section className="bg-charcoal py-16 sm:py-20 lg:py-24 grain relative" data-testid="capabilities-section">
                <div className="container-px">
                    <Reveal className="max-w-2xl">
                        <Eyebrow>Specialized Capabilities</Eyebrow>
                        <h2 className="mt-5 font-heading text-3xl sm:text-4xl font-bold tracking-tight text-white">
                            Beyond the Core Six.
                        </h2>
                        <p className="mt-5 text-base leading-relaxed text-gray-400">
                            The equipment and expertise to handle what standard carriers
                            can&rsquo;t — all under one roof.
                        </p>
                    </Reveal>
                    <ul className="mt-12 grid grid-cols-2 gap-px bg-white/10 sm:grid-cols-3 lg:grid-cols-4">
                        {CAPABILITIES.map((capability, i) => (
                            <Reveal key={capability} delay={(i % 4) * 0.05} className="bg-charcoal">
                                <li className="flex h-full items-center px-5 py-6 text-[14px] sm:text-[15px] font-semibold uppercase tracking-[0.1em] text-gray-200 transition-colors duration-200 hover:text-gold font-heading">
                                    {capability}
                                </li>
                            </Reveal>
                        ))}
                    </ul>
                </div>
            </section>

            <CTASection
                eyebrow="Get Started"
                title="Need a Logistics Solution?"
                copy="Tell us what you're moving and where it needs to go. Our team will help determine the right logistics solution."
            />
        </>
    );
}
